/**
 * DNBI Module - PSP/CRE Tool v3.0
 *
 * Handles Disease and Non-Battle Injury calculations including:
 * - Climate zone effects (Arctic, Tropical, Desert, etc.)
 * - Seasonal modifiers
 * - Altitude effects
 * - Mental health / Combat Stress Reaction
 * - Night operations effects
 * - Monte Carlo uncertainty modifiers (Phase 1.2)
 *
 * @version 5.6.0
 * @author Dr. Tatar Radu MD
 *
 * CHANGELOG v5.6.0 ENH-040 + ENH-045 (March 27, 2026):
 * - ENH-040: Non-linear CSR duration accumulation in calculateCSRCasualties().
 *   Replaces linear 1 + (day-1)×0.05 with piecewise formula using sustainable_days from mentalHealth.js.
 *   Before threshold: 0.03/day (slow — unit adapts, rotations partially effective).
 *   After threshold: 0.15/day (fast — fatigue cascade, no recovery, degraded cohesion).
 *   sustainable_days: PERMISSIVE=365, LOW=180, MODERATE=90, HIGH=30, EXTREME=14.
 * - ENH-045: FHP_LEVELS constant added to climateZones.js and imported here.
 *   fhpDNBIMod read from scenario.operation_parameters.fhp_config.fhp_level.
 *   Applied as multiplier in calculateComprehensiveDNBI() combinedModifier.
 *   NONE=1.25, MINIMAL=1.10, STANDARD=1.00 (default), OPTIMAL=0.75.
 *
 * CHANGELOG v5.5.0 ENH-034 (March 27, 2026):
 * - (previously logged here — see v5.4.0 / ENH-034 entry below)
 *
 * CHANGELOG v5.4.0 ENH-032 (March 27, 2026):
 * - WIRED: NIGHT_OPS_LEVELS.casualty_modifier from nightOperations.js into calculateComprehensiveDNBI().
 *   Sourced from scenario.operation_parameters.night_ops_config.modifiers (pre-computed by Tab2
 *   via getNightOpsModifiers()). Values: NONE=1.0, LIMITED=1.05, MODERATE=1.1, HIGH=1.15, CONTINUOUS=1.2.
 *   Applied as nightOpsCasualtyMod multiplier in combinedModifier alongside existing nightAnalysis.modifier.
 *   When nightOpsConfig.enabled=false or absent, nightOpsCasualtyMod=1.0 (no change).
 *
 * CHANGELOG v5.3.0 ENH-034 (March 27, 2026):
 * - WIRED: ENDEMIC_DISEASE_LEVELS.dnbi_modifier from climateZones.js into calculateClimateModifier().
 *   endemic_disease was already passed in from scenario but only carried in output — never multiplied.
 *   Now applied as endemicMod into combinedModifier and combinedDiseaseMod.
 * - WIRED: THEATER_REGIONS.dnbi_modifier from climateZones.js into calculateComprehensiveDNBI().
 *   theater_region now accepted in environment config; theater DNBI modifier applied to combinedModifier.
 * - UPDATED: casualtyCalculator.js passes theater_region from climateConfig to calculateComprehensiveDNBI().
 *
 * CHANGELOG v5.2.0 IMP-006 FIX (March 9, 2026):
 * - BUG FIX IMP-006: CSR_RATES was a hardcoded internal table, NOT imported from mentalHealth.js.
 *   Rates were 3–6× lower than research-validated constants at HIGH/EXTREME intensity:
 *     LOW_THREAT:      0.5 → 2.0 /1000/day  (+300%)
 *     MODERATE_THREAT: 1.5 → 5.0 /1000/day  (+233%)
 *     HIGH_THREAT:     3.0 → 15.0 /1000/day  (+400%)
 *     EXTREME_THREAT:  5.0 → 30.0 /1000/day  (+500%)
 *   RTD rates were fixed for all intensities (acute 85%, fatigue 60%, PTSD 20%).
 *   mentalHealth.js had intensity-dependent RTD (0.90 → 0.35) and breakdown entirely unused.
 * - FIX: Import CSR_RATES as MH_CSR_RATES from mentalHealth.js.
 * - FIX: THREAT_TO_INTENSITY_MAP maps old threatLevel vocabulary (LOW_THREAT / MODERATE_THREAT /
 *   HIGH_THREAT / EXTREME_THREAT) to mentalHealth.js BY_COMBAT_INTENSITY keys.
 * - FIX: getCSRData() reads rate, breakdown, and RTD from mentalHealth.js constants.
 * - FIX: Breakdown now derived from mentalHealth.js `presentation` object:
 *     acute_stress → presentation.acute_stress (direct)
 *     combat_fatigue ← presentation.adjustment_disorder + dissociative
 *     ptsd_onset ← presentation.ptsd_symptoms + conversion + psychotic_features + anxiety_disorder + other
 *   Verified sums to 1.0 at all 5 intensity levels.
 * - FIX: RTD rates now intensity-derived from mentalHealth.js rtd_rate_72h:
 *     acute_stress_rtd_percent = rtd_rate_72h × 100
 *     combat_fatigue_rtd_percent = rtd_rate_72h × 70
 *     ptsd_rtd_percent = max(10, rtd_rate_72h × 25)
 * - ADDED: sustainable_days from mentalHealth.js exposed in return object.
 * - Exported CSR_RATES const rebuilt from mentalHealth.js data (backward compat).
 *
 * CHANGELOG v5.1 DNBI-01 FIX (February 22, 2026) — Phase 5:
 * - BUG FIX DNBI-01: CSR_RATES key vocabulary misaligned with operationsConfig.js THREAT_LEVELS.
 * - RENAMED: `MEDIUM_THREAT` → `MODERATE_THREAT` (matches operationsConfig.js)
 * - RENAMED: `INTENSE` → `EXTREME_THREAT` (matches operationsConfig.js)
 * - REMOVED: `PEER` key — not a value in THREAT_LEVELS; was overlapping with HIGH_THREAT/EXTREME_THREAT.
 *   PEER is a threat category (type of adversary), not a threat intensity level. Callers that
 *   passed 'PEER' will now fall through to the MODERATE_THREAT default, which is correct behaviour.
 * - Updated default parameter in calculateCSRCasualties(): 'MEDIUM_THREAT' → 'MODERATE_THREAT'
 * - Updated fallback in calculateCSRCasualties() line ~537: CSR_RATES.MEDIUM_THREAT → CSR_RATES.MODERATE_THREAT
 * - Updated default in calculateComprehensiveDNBI() threat_level fallback: 'MEDIUM_THREAT' → 'MODERATE_THREAT'
 * - DNBI-02 CONFIRMED: rtd.expected_rtd (line 834) correctly computed by category loop
 *   lines 812–820. No code change required. Output is valid for P1-02/P1-03 consumption.
 *
 * CHANGELOG v5.0.2 P1-01 FIX (February 22, 2026):
 * - BUG FIX P1-01: Wrong property key in DNBI_RATES_BY_OPERATIONAL_STATE lookup.
 * - OLD: DNBI_RATES_BY_OPERATIONAL_STATE?.[state]?.rate_per_1000_per_day
 * - NEW: DNBI_RATES_BY_OPERATIONAL_STATE?.[state]?.combined_rate
 * - KEY `rate_per_1000_per_day` does not exist in dnbiRates.js; key is `combined_rate`.
 * - With wrong key, lookup always returned undefined → hardcoded fallback fired 100%
 *   of the time → DNBI rates never read from research-validated constants.
 * - Result: stateRate now reads correctly from dnbiRates.js constants file.
 *
 * CHANGELOG v5.0.1 CRITICAL PATH FIX (February 16, 2026):
 * - BUG FIX: Monte Carlo modifier was reading from wrong location
 * - OLD: scenario.monte_carlo_modifiers.dnbi_modifier (doesn't exist)
 * - NEW: scenario.operation_parameters.monte_carlo_modifiers.dnbi_modifier
 * - IMPACT: Modifier always defaulted to 1.0 → no variance in Monte Carlo results
 * - RESULT: Now reads correct path → percentiles will show real variance
 *
 * CHANGELOG v5.0 PHASE 1.2: Monte Carlo Modifier Integration (February 16, 2026):
 * - NEW: Reads scenario.monte_carlo_modifiers.dnbi_modifier
 * - NEW: Applies Monte Carlo variance to DNBI rate calculations
 * - LOCATION: calculateComprehensiveDNBI() line ~700
 * - IMPACT: Enables uncertainty quantification for DNBI projections
 * - PATTERN: Modifier = 1.0 (deterministic), 0.75-1.25 range (Monte Carlo ±25%)
 * - INTEGRATION: Works with monteCarloSimulator.js VARIANCE_PARAMETERS
 *
 * CHANGELOG v4.3 (January 31, 2026):
 * - TASK 3.0 FIX: Corrected operational_state DNBI rate fallback values
 *   - COMBAT_ENGAGED: 2.0 → 4.5 per 1000/day (was 56% too low!)
 *   - SUPPORT_ACTIVE: 1.0 → 1.5 per 1000/day (was 33% too low)
 *   - TRUE_RESERVE: 0.7 → 0.5 per 1000/day (was 40% too high)
 *   - Added explicit TRUE_RESERVE case in fallback
 *   - Default fallback: 4.5 (Combat Engaged) instead of 0.7
 *   - CRITICAL: If imports fail, fallbacks now use correct DNBI rates
 *   - Ensures Support Active has 3x fewer DNBI than Combat Engaged
 *   - Ensures True Reserve has 9x fewer DNBI than Combat Engaged
 *
 * CHANGELOG v4.2 (January 21, 2026):
 * - Added BUG-046 diagnostics: Console warnings trace exact source of NaN
 * - Safe fallbacks prevent NaN cascade (strength, rate, CSR all protected)
 * - Now outputs specific warning showing WHICH variable is NaN
 *
 * CHANGELOG v4.1 (January 21, 2026):
 * - FIX: "DNBI category distribution mismatch" warning
 * - Replaced naive Math.round() distribution with Largest Remainder Method
 * - This ensures category totals EXACTLY equal nonCSRDNBI (no rounding errors)
 * - The Largest Remainder Method is the standard algorithm for proportional
 *   integer distribution (also known as Hamilton's method)
 */

// ============================================================================
// IMPORTS
// ============================================================================

// CALC-010: required-constant reader (throws with the path instead of a literal fallback)
import { PSYCHIATRIC_SUPPORT_LEVELS, PSYCHIATRIC_RTD72_CAP } from '../../constants/psychiatricSupport.js';
import { requireNumber } from '../requireValue.js';

import {
  DNBI_RATES_BY_OPERATIONAL_STATE,
  DNBI_RATES_BY_ECHELON,
  DNBI_ENVIRONMENTAL_MODIFIERS,
  DNBI_CATEGORIES,
  BEHAVIORAL_HEALTH_SOURCE_CATEGORY,   // CALC-014
  COLD_INJURY_MODEL,
  HEAT_INJURY_MODEL,
  DNBI_WOJCIK_PHASE_RATES,
  DNBI_WOJCIK_POSTURE_MAP
} from '../../constants/dnbiRates.js';

import {
  getDNBIRate as getStateDNBIRate
} from '../../constants/operationalStates.js';

import {
  UNIT_TYPES
} from '../../constants/unitTypes.js';

// IMP-006: Import research-validated CSR rates from mentalHealth.js constants.
// These replace the previous internal hardcoded CSR_RATES table which was
// 3–6× lower than the research-validated values at HIGH/EXTREME intensity.
import {
  CSR_RATES as MH_CSR_RATES,
  CSR_WIA_MODEL,
  CSR_COHESION_FACTORS,
  MORALE_TO_COHESION
} from '../../constants/mentalHealth.js';

// ENH-034: Import endemic disease and theater region DNBI modifiers from climateZones.js.
// ENDEMIC_DISEASE_LEVELS.dnbi_modifier adjusts DNBI for specific disease burden (NONE→EPIDEMIC).
// THEATER_REGIONS.dnbi_modifier adjusts DNBI for theater-specific disease profile.
// ENH-045: FHP_LEVELS.dnbi_modifier adjusts DNBI for Force Health Protection program level.
import {
  ENDEMIC_DISEASE_LEVELS,
  THEATER_REGIONS,
  FHP_LEVELS
} from '../../constants/climateZones.js';

// V4-007: CBRN DNBI elevation multiplier.
// Source: AJMedP-7.5 Table 3; NATO STANAG 2553 (AMedP-7.5).
// MOPP conditions (heat stress, dehydration, psychological burden, secondary contamination casualties)
// elevate DNBI above baseline. Multipliers stored in CBRN_THREAT_LEVELS[level].dnbi_multiplier.
import {
  CBRN_THREAT_LEVELS
} from '../../constants/cbrnMedical.js';
import { THREAT_EFFECTS_USE, threatTypeDnbiFactor, infrastructureModel } from '../../constants/threatEffects.js';
import { roundWithCarry } from './fractionCarry.js';

// ============================================================================
// CLIMATE DATA - Now using DNBI_ENVIRONMENTAL_MODIFIERS from dnbiRates.js
// ============================================================================
// The comprehensive climate data including:
// - TEMPERATE, CARPATHIAN (Romania), EASTERN_EUROPEAN_WINTER (Ukraine)
// - TROPICAL_WET, TROPICAL_DRY, ARCTIC, HIGH_ALTITUDE
// - Monthly modifiers (12 months), terrain types, sanitation levels
// - Cold injury model, heat injury model
// Is imported from dnbiRates.js as DNBI_ENVIRONMENTAL_MODIFIERS

// Climate zone mapping for compatibility with climateZones.js UI keys
const CLIMATE_ZONE_MAPPING = {
  // Direct matches
  'TEMPERATE': 'TEMPERATE',
  'TROPICAL_WET': 'TROPICAL_WET',
  'TROPICAL_DRY': 'TROPICAL_DRY',
  'ARCTIC': 'ARCTIC',
  'CARPATHIAN': 'CARPATHIAN',
  'EASTERN_EUROPEAN_WINTER': 'EASTERN_EUROPEAN_WINTER',
  'HIGH_ALTITUDE': 'HIGH_ALTITUDE',
  // Mappings from climateZones.js keys to dnbiRates.js keys
  'MOUNTAINOUS': 'HIGH_ALTITUDE',
  'SUBTROPICAL': 'TEMPERATE',
  'DESERT': 'TROPICAL_DRY',
  'MOUNTAIN': 'HIGH_ALTITUDE',
  'JUNGLE': 'TROPICAL_WET'
};

/**
 * Get climate data from DNBI_ENVIRONMENTAL_MODIFIERS with fallback
 * @param {string} climateZone - Climate zone key from UI
 * @returns {Object} Climate data from dnbiRates.js
 */
function getClimateData(climateZone) {
  const mappedKey = CLIMATE_ZONE_MAPPING[climateZone] || 'TEMPERATE';
  return DNBI_ENVIRONMENTAL_MODIFIERS?.CLIMATE?.[mappedKey] ||
         DNBI_ENVIRONMENTAL_MODIFIERS?.CLIMATE?.TEMPERATE ||
         { modifier: 1.0, disease_modifier: 1.0, injury_modifier: 1.0 };
}

/**
 * Get terrain data from DNBI_ENVIRONMENTAL_MODIFIERS
 * @param {string} terrain - Terrain type
 * @returns {Object} Terrain modifier data
 */
function getTerrainData(terrain) {
  const terrainKey = terrain?.toUpperCase() || 'OPEN';
  return DNBI_ENVIRONMENTAL_MODIFIERS?.TERRAIN?.[terrainKey] ||
         DNBI_ENVIRONMENTAL_MODIFIERS?.TERRAIN?.OPEN ||
         { modifier: 1.0 };
}

/**
 * Get sanitation data from DNBI_ENVIRONMENTAL_MODIFIERS
 * Maps from climateZones.js WATER_FOOD_SAFETY keys to dnbiRates.js WATER_SANITATION keys
 * @param {string} sanitation - Sanitation level (SAFE/CAUTION/UNSAFE or EXCELLENT/GOOD/etc)
 * @returns {Object} Sanitation modifier data
 */
function getSanitationData(sanitation) {
  // Map from climateZones.js WATER_FOOD_SAFETY to dnbiRates.js WATER_SANITATION
  const SANITATION_MAPPING = {
    // climateZones.js keys → dnbiRates.js keys
    'SAFE': 'GOOD',
    'CAUTION': 'ADEQUATE',
    'UNSAFE': 'VERY_POOR',
    // Direct matches (if using dnbiRates keys directly)
    'EXCELLENT': 'EXCELLENT',
    'GOOD': 'GOOD',
    'ADEQUATE': 'ADEQUATE',
    'POOR': 'POOR',
    'VERY_POOR': 'VERY_POOR'
  };

  const inputKey = sanitation?.toUpperCase() || 'GOOD';
  const mappedKey = SANITATION_MAPPING[inputKey] || 'GOOD';

  return DNBI_ENVIRONMENTAL_MODIFIERS?.WATER_SANITATION?.[mappedKey] ||
         DNBI_ENVIRONMENTAL_MODIFIERS?.WATER_SANITATION?.GOOD ||
         { modifier: 1.0, disease_modifier: 1.0 };
}

/**
 * Altitude effects on DNBI
 * Source: US Army FM 4-02.12
 */
const ALTITUDE_EFFECTS = {
  LOW: { threshold_m: 1500, modifier: 1.0, ams_risk: 0 },
  MODERATE: { threshold_m: 2500, modifier: 1.2, ams_risk: 0.10 },
  HIGH: { threshold_m: 3500, modifier: 1.5, ams_risk: 0.25 },
  VERY_HIGH: { threshold_m: 5500, modifier: 2.0, ams_risk: 0.50 },
  EXTREME: { threshold_m: Infinity, modifier: 2.5, ams_risk: 0.75 }
};

/**
 * Night operations DNBI effects
 */
const NIGHT_OPERATIONS_DNBI = {
  accident_rate_modifier: 1.4,        // 40% more accidents
  fatigue_modifier: 1.2,              // 20% more fatigue-related
  cold_injury_modifier: 1.5,          // 50% more cold injuries (no sun)
  mental_health_modifier: 1.15,       // 15% more stress reactions
  overall_modifier: 1.25              // Combined 25% increase
};

// ============================================================================
// IMP-006 FIX: CSR DATA ADAPTER FROM mentalHealth.js
// ============================================================================

/**
 * Maps dnbiModule threatLevel vocabulary → mentalHealth.js BY_COMBAT_INTENSITY keys.
 *
 * The module's callers use operationsConfig.js THREAT_LEVELS vocabulary
 * (LOW_THREAT / MODERATE_THREAT / HIGH_THREAT / EXTREME_THREAT).
 * mentalHealth.js uses a parallel intensity vocabulary for the same concept.
 * This map bridges the two without changing any caller.
 */
const THREAT_TO_INTENSITY_MAP = {
  LOW_THREAT:      'LOW_INTENSITY',
  MODERATE_THREAT: 'MODERATE_INTENSITY',
  HIGH_THREAT:     'HIGH_INTENSITY',
  EXTREME_THREAT:  'EXTREME_INTENSITY',
  // Pass-through if caller already uses intensity vocabulary
  PERMISSIVE:         'PERMISSIVE',
  LOW_INTENSITY:      'LOW_INTENSITY',
  MODERATE_INTENSITY: 'MODERATE_INTENSITY',
  HIGH_INTENSITY:     'HIGH_INTENSITY',
  EXTREME_INTENSITY:  'EXTREME_INTENSITY'
};

/**
 * Adapts mentalHealth.js CSR data for use in calculateCSRCasualties().
 *
 * Mapping logic:
 *   rate_per_1000_per_day  = mh.daily_csr_rate × 1000
 *   breakdown.acute_stress = mh.presentation.acute_stress
 *   breakdown.combat_fatigue = mh.presentation.adjustment_disorder + dissociative
 *   breakdown.ptsd_onset = mh.presentation.ptsd_symptoms + conversion +
 *                          psychotic_features + anxiety_disorder + other
 *   (verified: sums to 1.0 at all 5 intensity levels)
 *
 * RTD rates derived from mh.rtd_rate_72h (BICEPS 72-hour window):
 *   acute_stress_rtd_percent  = rtd_rate_72h × 100   (best BICEPS responders)
 *   combat_fatigue_rtd_percent = rtd_rate_72h × 70   (partial responders)
 *   ptsd_rtd_percent          = max(10, rtd_rate_72h × 25) (worst prognosis, floor 10%)
 *
 * @param {string} threatLevel - Caller's threat level key (LOW_THREAT / MODERATE_THREAT / etc.)
 * @returns {Object} CSR data shaped for calculateCSRCasualties()
 */
function getCSRData(threatLevel) {
  const intensityKey = THREAT_TO_INTENSITY_MAP[threatLevel] || 'MODERATE_INTENSITY';
  const mh = MH_CSR_RATES?.BY_COMBAT_INTENSITY?.[intensityKey];

  if (!mh) {
    // Fallback — should never fire; mentalHealth.js always has MODERATE_INTENSITY
    return {
      rate_per_1000_per_day: 5.0,
      breakdown: { acute_stress: 0.45, combat_fatigue: 0.35, ptsd_onset: 0.20 },
      rtd_rate_72h: 0.65,
      evacuation_rate: 0.10,
      sustainable_days: 90,
      intensity_key: 'MODERATE_INTENSITY'
    };
  }

  const p = mh.presentation || {};

  // Map mentalHealth.js presentation keys → 3-category breakdown used by this module.
  // All fractional fields default to 0 for intensity levels that lack them
  // (e.g. PERMISSIVE has no ptsd_symptoms — that mass goes into ptsd_onset via 'other').
  const acuteStress   = p.acute_stress        || 0;
  const combatFatigue = (p.adjustment_disorder || 0) + (p.dissociative || 0);
  const ptsdOnset     = (p.ptsd_symptoms       || 0)
                      + (p.conversion          || 0)
                      + (p.psychotic_features  || 0)
                      + (p.anxiety_disorder    || 0)
                      + (p.other               || 0);

  // RTD rates scaled from intensity-specific rtd_rate_72h (BICEPS 72h window)
  const rtd72h = mh.rtd_rate_72h || 0.65;
  const acuteRTD   = Math.round(rtd72h * 100);
  const fatigueRTD = Math.round(rtd72h * 70);
  const ptsdRTD    = Math.max(10, Math.round(rtd72h * 25));

  return {
    rate_per_1000_per_day: mh.daily_csr_rate * 1000,
    breakdown: {
      acute_stress:   acuteStress,
      combat_fatigue: combatFatigue,
      ptsd_onset:     ptsdOnset
    },
    rtd_rate_72h:     rtd72h,
    wia_ratio:        mh.wia_ratio,
    evacuation_rate:  mh.evacuation_rate   || 0.10,
    sustainable_days: mh.sustainable_days  || 90,
    intensity_key:    intensityKey,
    // Pre-computed RTD percents for use in calculateCSRCasualties()
    acute_stress_rtd_percent:   acuteRTD,
    combat_fatigue_rtd_percent: fatigueRTD,
    ptsd_rtd_percent:           ptsdRTD
  };
}

/**
 * CSR_RATES — backward-compatible export, rebuilt from mentalHealth.js data.
 *
 * Exposes the same key vocabulary (LOW_THREAT / MODERATE_THREAT / HIGH_THREAT /
 * EXTREME_THREAT) that external code may reference, but values now come from
 * the research-validated mentalHealth.js constants.
 */
const CSR_RATES = Object.fromEntries(
  Object.keys(THREAT_TO_INTENSITY_MAP)
    .filter(k => ['LOW_THREAT', 'MODERATE_THREAT', 'HIGH_THREAT', 'EXTREME_THREAT'].includes(k))
    .map(k => [k, getCSRData(k)])
);

/**
 * DNBI category breakdown (baseline)
 * Source: NATO AMedP-8(C)
 */
// CALC-014 (2026-10-09): the inline fallback object that used to follow `DNBI_CATEGORIES ||` was dead
// (the constant always exists) and used a different category set; removed.
const DNBI_CATEGORY_BREAKDOWN = DNBI_CATEGORIES;
/** Protective efficacy of prophylaxis taken as prescribed (R2-06). EDUCATED GUESS: docs/research/RESEARCH_PROPHYLAXIS.md. */
export const PROPHYLAXIS_EFFICACY = 0.8;
export const PROPHYLAXIS_MAX_SCALE = 5;

// ============================================================================
// CORE FUNCTIONS
// ============================================================================

/**
 * Calculate climate zone DNBI modifier
 *
 * @param {Object} config - Configuration object
 * @param {string} config.climateZone - Climate zone identifier
 * @param {string} config.season - Current season
 * @param {number} config.altitude_m - Altitude in meters
 * @returns {Object} Climate analysis with modifier
 */
export function calculateClimateModifier(config) {
  const {
    climateZone = 'TEMPERATE',
    season = 'SUMMER',
    month = null,           // NEW: for monthly precision (JAN, FEB, etc.)
    altitude_m = 500,
    terrain = 'OPEN',       // NEW: from DNBI_ENVIRONMENTAL_MODIFIERS.TERRAIN
    sanitation = 'GOOD',    // NEW: from DNBI_ENVIRONMENTAL_MODIFIERS.WATER_SANITATION
    endemic_disease = 'NONE', // For educational output
    prophylaxis_compliance = 1.0
  } = config;

  // Get comprehensive climate data from dnbiRates.js
  const climateData = getClimateData(climateZone);

  // Get terrain modifier
  const terrainData = getTerrainData(terrain);

  // Get sanitation modifier
  const sanitationData = getSanitationData(sanitation);

  // Base modifier from climate
  const baseClimateMod = climateData.modifier || 1.0;

  // Disease and injury specific modifiers
  const diseaseModifier = climateData.disease_modifier || 1.0;
  const injuryModifier = climateData.injury_modifier || 1.0;

  // Apply seasonal/monthly modifier
  // Prefer monthly if available (12 months vs 4 seasons - more accurate)
  let seasonalMod = 1.0;
  let seasonSource = 'default';

  if (month && climateData.monthly_modifiers) {
    // Use monthly precision if month is provided
    seasonalMod = climateData.monthly_modifiers[month.toUpperCase()] || 1.0;
    seasonSource = 'monthly';
  } else if (season) {
    // Map season to month or use seasonal data if available
    const seasonToMonth = {
      'SPRING': 'APR', 'spring': 'APR',
      'SUMMER': 'JUL', 'summer': 'JUL',
      'AUTUMN': 'OCT', 'FALL': 'OCT', 'autumn': 'OCT', 'fall': 'OCT',
      'WINTER': 'JAN', 'winter': 'JAN',
      'DRY_SEASON': 'FEB', 'WET_SEASON': 'AUG'
    };

    if (climateData.monthly_modifiers) {
      const mappedMonth = seasonToMonth[season] || 'JUL';
      seasonalMod = climateData.monthly_modifiers[mappedMonth] || 1.0;
      seasonSource = 'seasonal-to-monthly';
    }
  }

  // Altitude modifier
  let altitudeCategory = 'LOW';
  let altitudeModifier = 1.0;
  let amsRisk = 0;

  for (const [category, data] of Object.entries(ALTITUDE_EFFECTS)) {
    if (altitude_m < data.threshold_m) {
      altitudeCategory = category;
      altitudeModifier = data.modifier;
      amsRisk = data.ams_risk;
      break;
    }
  }

  // Terrain modifier from DNBI_ENVIRONMENTAL_MODIFIERS
  const terrainMod = terrainData.modifier || 1.0;
  const terrainDiseaseMod = terrainData.disease_modifier || 1.0;
  const terrainInjuryMod = terrainData.injury_modifier || 1.0;

  // Sanitation modifier
  const sanitationMod = sanitationData.modifier || 1.0;
  const sanitationDiseaseMod = sanitationData.disease_modifier || 1.0;

  // ENH-034: Endemic disease modifier from climateZones.js ENDEMIC_DISEASE_LEVELS.
  // User selects endemic level in Tab2 — applies on top of climate zone baseline.
  const endemicData = ENDEMIC_DISEASE_LEVELS?.[endemic_disease] || ENDEMIC_DISEASE_LEVELS?.NONE;
  // R2-06 (2026-10-21): prophylaxis compliance. The endemic modifier of the table is the burden with every soldier taking the prophylaxis (compliance 1.0, the
  // Tab 2 default, so default numbers are unchanged); with lower compliance the endemic excess grows by (1 - eff x c) / (1 - eff), capped at 5. eff = protective
  // efficacy of malaria chemoprophylaxis when taken as prescribed, 0.8 (EDUCATED GUESS inside the trial range: doxycycline 84, mefloquine 77, atovaquone-proguanil
  // 96 to 100 percent suppression of parasitaemia, Cochrane review of travellers; field compliance 56 to 80 percent: Somalia 1993, Afghanistan 2006-2007).
  // Only for endemic levels that require prophylaxis.
  if (typeof prophylaxis_compliance !== 'number' || Number.isNaN(prophylaxis_compliance) || prophylaxis_compliance < 0 || prophylaxis_compliance > 1) {
    throw new Error(`dnbiModule.calculateClimateModifier: prophylaxis_compliance must be between 0 and 1 (got: ${prophylaxis_compliance})`);
  }
  const _rawEndemic = endemicData?.dnbi_modifier ?? 1.0;
  const _prophylaxisScale = endemicData?.prophylaxis_required
    ? Math.min(PROPHYLAXIS_MAX_SCALE, (1 - PROPHYLAXIS_EFFICACY * prophylaxis_compliance) / (1 - PROPHYLAXIS_EFFICACY))
    : 1;
  const endemicMod = 1 + (_rawEndemic - 1) * _prophylaxisScale;

  // Combine modifiers (multiplicative)
  const combinedModifier = baseClimateMod * seasonalMod * altitudeModifier * terrainMod * sanitationMod * endemicMod;

  // Disease-specific combined modifier
  const combinedDiseaseMod = diseaseModifier * terrainDiseaseMod * sanitationDiseaseMod * seasonalMod * endemicMod;

  // Injury-specific combined modifier
  const combinedInjuryMod = injuryModifier * terrainInjuryMod * altitudeModifier;

  // Calculate specific risks from COLD_INJURY_MODEL and HEAT_INJURY_MODEL
  const specificRisks = [];

  // Check for cold injury risk
  if (COLD_INJURY_MODEL && (climateZone.includes('ARCTIC') || climateZone.includes('WINTER') ||
      climateZone === 'CARPATHIAN' || season?.toUpperCase() === 'WINTER')) {
    specificRisks.push({
      type: 'COLD_INJURY',
      rate_per_1000: (requireNumber(COLD_INJURY_MODEL.temperature_thresholds?.WARNING?.rate_per_1000_day, 'dnbiModule.js:535', 'COLD_INJURY_MODEL.temperature_thresholds.WARNING.rate_per_1000_day')) * seasonalMod,
      note: 'Cold injury risk elevated - implement prevention protocols'
    });
  }

  // Check for heat injury risk
  if (HEAT_INJURY_MODEL && (climateZone.includes('TROPICAL') || climateZone.includes('DESERT') ||
      climateZone === 'TROPICAL_DRY' || season?.toUpperCase() === 'SUMMER')) {
    specificRisks.push({
      type: 'HEAT_INJURY',
      rate_per_1000: requireNumber(HEAT_INJURY_MODEL?.baseline?.rate_per_1000_day,
        'dnbiModule.calculateClimateModifier', 'HEAT_INJURY_MODEL.baseline.rate_per_1000_day') * seasonalMod,
      note: 'Heat injury risk - enforce hydration discipline'
    });
  }

  // Altitude sickness risk
  if (amsRisk > 0) {
    specificRisks.push({
      type: 'ALTITUDE_SICKNESS',
      rate_per_1000: amsRisk * 1000 / 30, // Per day, assuming 30-day deployment
      note: `${Math.round(amsRisk * 100)}% risk of AMS at ${altitude_m}m`
    });
  }

  return {
    climate_zone: climateZone,
    mapped_zone: CLIMATE_ZONE_MAPPING[climateZone] || 'TEMPERATE',
    season,
    month,
    season_source: seasonSource,
    altitude_m,
    altitude_category: altitudeCategory,
    terrain,
    sanitation,
    endemic_disease, // Added for educational output

    modifiers: {
      climate_base: baseClimateMod,
      seasonal: Math.round(seasonalMod * 100) / 100,
      altitude: altitudeModifier,
      terrain: terrainMod,
      sanitation: sanitationMod,
      combined: Math.round(combinedModifier * 100) / 100,
      disease_combined: Math.round(combinedDiseaseMod * 100) / 100,
      injury_combined: Math.round(combinedInjuryMod * 100) / 100
    },

    primary_risks: climateData.primary_risks || [],
    specific_risk_rates: specificRisks,
    ams_risk_percent: Math.round(amsRisk * 100),

    // Special conditions
    trench_conditions: climateData.trench_conditions || null,
    acclimatization_days: climateData.acclimatization_days || 0,
    ukraine_validated: climateData.ukraine_validated || false,

    educational_note: `Climate: ${climateZone} (${seasonSource} modifier: ${seasonalMod.toFixed(2)}). ` +
      `Terrain: ${terrain} (${terrainMod.toFixed(2)}×). Sanitation: ${sanitation} (${sanitationMod.toFixed(2)}×). ` +
      `Endemic Disease: ${endemic_disease}. ` +
      `Combined DNBI modifier: ${combinedModifier.toFixed(2)}×`,

    recommendations: generateClimateRecommendations(climateZone, season, altitudeCategory)
  };
}

/**
 * Generate climate-specific recommendations
 */
function generateClimateRecommendations(zone, season, altitude) {
  const recs = [];

  if (zone === 'ARCTIC' || (zone === 'TEMPERATE' && season === 'WINTER')) {
    recs.push('Issue cold weather injury prevention kits');
    recs.push('Implement buddy system for cold injury checks');
    recs.push('Rotate troops from exposed positions every 2 hours');
  }

  if (zone === 'DESERT' || (zone === 'TROPICAL_DRY' && season === 'DRY_SEASON')) {
    recs.push('Enforce hydration discipline - 1L/hour during activity');
    recs.push('Schedule operations for early morning/evening');
    recs.push('Pre-position cooling stations');
  }

  if (zone === 'TROPICAL_WET' || zone === 'JUNGLE') {
    recs.push('Ensure malaria prophylaxis compliance 100%');
    recs.push('Issue insect repellent and bed nets');
    recs.push('Daily foot inspections mandatory');
  }

  if (altitude === 'HIGH' || altitude === 'VERY_HIGH' || altitude === 'EXTREME') {
    recs.push('Allow 48-72 hours for acclimatization');
    recs.push('Acetazolamide prophylaxis recommended');
    recs.push('Descend immediately if AMS symptoms develop');
  }

  return recs;
}

/**
 * Calculate night operations DNBI effect
 *
 * @param {boolean} isNight - Whether operating at night
 * @param {Object} config - Additional configuration
 * @returns {Object} Night operations DNBI analysis
 */
export function calculateNightOperationsDNBI(isNight, config = {}) {
  if (!isNight) {
    return {
      is_night: false,
      modifier: 1.0,
      impact: 'None - daylight operations'
    };
  }

  const {
    hasNVG = true,
    operationDuration = 8,
    consecutiveNights = 1
  } = config;

  // Base night modifier
  let modifier = NIGHT_OPERATIONS_DNBI.overall_modifier;

  // NVG reduces some effects
  if (hasNVG) {
    modifier *= 0.85; // 15% reduction in DNBI if equipped
  }

  // Cumulative fatigue for consecutive nights
  if (consecutiveNights > 1) {
    modifier *= 1 + (consecutiveNights - 1) * 0.1; // 10% per additional night
  }

  // Long operations increase accidents
  if (operationDuration > 12) {
    modifier *= 1.2;
  }

  return {
    is_night: true,
    modifier: Math.round(modifier * 100) / 100,

    breakdown: {
      accident_increase: Math.round((NIGHT_OPERATIONS_DNBI.accident_rate_modifier - 1) * 100),
      fatigue_increase: Math.round((NIGHT_OPERATIONS_DNBI.fatigue_modifier - 1) * 100),
      cold_injury_increase: Math.round((NIGHT_OPERATIONS_DNBI.cold_injury_modifier - 1) * 100),
      mental_health_increase: Math.round((NIGHT_OPERATIONS_DNBI.mental_health_modifier - 1) * 100)
    },

    factors: {
      nvg_equipped: hasNVG,
      operation_duration_hours: operationDuration,
      consecutive_nights: consecutiveNights
    },

    recommendations: [
      'Rotate personnel between night/day shifts',
      consecutiveNights > 2 ? 'CRITICAL: Mandate 24h rest after 3 consecutive nights' : null,
      !hasNVG ? 'Issue NVG to reduce accident rate' : null
    ].filter(Boolean)
  };
}

/**
 * Calculate Combat Stress Reaction (CSR) casualties.
 *
 * IMP-006 FIX: CSR data now sourced from mentalHealth.js research-validated
 * constants via getCSRData(). Rates are 3–6× higher than the previous internal
 * table at HIGH/EXTREME intensity. RTD rates are intensity-dependent (derived
 * from mentalHealth.js rtd_rate_72h) rather than fixed for all intensities.
 *
 * @param {Object} config - Configuration object
 * @param {number} config.strength - Unit strength
 * @param {string} config.threatLevel - Threat level (LOW_THREAT / MODERATE_THREAT / HIGH_THREAT / EXTREME_THREAT)
 * @param {number} config.dayOfOperation - Day of operation
 * @param {number} config.cumulativeBattleCasualties - Battle casualties so far
 * @param {string} config.unitCohesion - Unit cohesion level
 * @returns {Object} CSR analysis
 */
export function calculateCSRCasualties(config) {
  const {
    strength = 4500,
    threatLevel = 'MODERATE_THREAT',
    dayOfOperation = 1,
    cumulativeBattleCasualties = 0,
    woundedToday = 0,                 // WIA of the day: combat stress follows the wounded (CSR_WIA_MODEL)
    unitCohesion = 'GOOD',
    psychiatricSupport = 'STANDARD'   // Tab 3 planning assumption (constants/psychiatricSupport.js)
  } = config;

  // IMP-006 FIX: Get CSR data from mentalHealth.js via adapter (was hardcoded internal table)
  const csrData = getCSRData(threatLevel);
  // Psychiatric support (2026-10-10): more support returns more combat stress casualties to duty within 72 h, so fewer
  // are evacuated. STANDARD (multiplier 1.0) is the reference of the sourced rates and changes nothing.
  const supportLevel = PSYCHIATRIC_SUPPORT_LEVELS[psychiatricSupport];
  if (!supportLevel) {
    throw new Error(`dnbiModule.calculateCSRCasualties: unknown psychiatric support '${psychiatricSupport}' (valid: ${Object.keys(PSYCHIATRIC_SUPPORT_LEVELS).join(', ')})`);
  }
  const withSupport = (rtdPercent) =>
    Math.min(PSYCHIATRIC_RTD72_CAP * 100, Math.round(rtdPercent * supportLevel.rtd72Multiplier * 10) / 10);

  // ENH-040: Non-linear CSR duration accumulation using sustainable_days from mentalHealth.js.
  // Before sustainable_days threshold: slow accumulation at 0.03/day (unit adapts, rotations help).
  // After threshold: accelerated at 0.15/day (fatigue, attrition, no recovery available).
  // sustainable_days: PERMISSIVE=365d, LOW=180d, MODERATE=90d, HIGH=30d, EXTREME=14d.
  // Replaces previous linear 0.05/day which underestimated early accumulation at extreme intensity
  // and overestimated late accumulation at low intensity (PERMISSIVE: day 30 → was ×2.45, now ×1.87).
  const sustainableDays = csrData.sustainable_days || 90;
  const growthPerDay = CSR_WIA_MODEL.duration_growth_per_day.value;                       // PLACEHOLDER (mentalHealth.js)
  const growthAfterSustainable = CSR_WIA_MODEL.duration_growth_after_sustainable_per_day.value; // PLACEHOLDER
  const durationFactor = dayOfOperation <= sustainableDays
    ? 1 + (dayOfOperation - 1) * growthPerDay
    : (1 + (sustainableDays - 1) * growthPerDay) + (dayOfOperation - sustainableDays) * growthAfterSustainable;

  // Battle casualty exposure (2026-10-10, docs/research/CSR_VOLUME_CHECK.md option A): cases = baseline psychiatric rate x strength
  // + wia_ratio x wounded of the day. The old multiplier 1 + 2 x cumulative casualties / strength is replaced by the
  // wounded-anchored term, so exposure is no longer counted twice.
  const casualtyExposureFactor = 1;

  // Unit cohesion factor. Accepts a cohesion key (EXCELLENT ... BROKEN) or a Tab 1 morale level (FANATICAL ... ROUTED).
  // An unknown key throws: it used to fall silently to 1.0, which made every morale except BROKEN a no-op.
  const cohesionKey = MORALE_TO_COHESION[unitCohesion] || unitCohesion;
  const cohesionFactor = CSR_COHESION_FACTORS[cohesionKey];
  if (cohesionFactor === undefined) {
    throw new Error(`dnbiModule.calculateCSRCasualties: unknown unit cohesion / morale '${unitCohesion}' (valid: ${[...Object.keys(MORALE_TO_COHESION), ...Object.keys(CSR_COHESION_FACTORS)].join(', ')})`);
  }

  // Calculate adjusted rate
  const baselinePerDay = (strength / 1000) * CSR_WIA_MODEL.baseline_per_1000_per_day;
  const woundedAnchored = csrData.wia_ratio * woundedToday;
  const stressMultiplier = durationFactor * casualtyExposureFactor * cohesionFactor;
  const csrUnrounded = (baselinePerDay + woundedAnchored) * stressMultiplier;
  const csrCasualties = Math.round(csrUnrounded);
  const adjustedRate = strength > 0 ? (csrCasualties / strength) * 1000 : 0;

  // Breakdown by type — fractions from mentalHealth.js presentation (mapped via getCSRData)
  const breakdown = {
    acute_stress_reaction: Math.round(csrCasualties * csrData.breakdown.acute_stress),
    combat_fatigue:        Math.round(csrCasualties * csrData.breakdown.combat_fatigue),
    ptsd_onset:            Math.round(csrCasualties * csrData.breakdown.ptsd_onset)
  };

  // IMP-006 FIX: RTD expectations now intensity-dependent from mentalHealth.js rtd_rate_72h.
  // Previous values were fixed constants (acute 85%, fatigue 60%, PTSD 20%) regardless
  // of intensity. At EXTREME intensity the correct values are ~35% / ~24% / ~10%.
  const rtdExpectations = {
    acute_stress_rtd_percent:   withSupport(csrData.acute_stress_rtd_percent),    // mentalHealth rtd_rate_72h × 100, × support
    combat_fatigue_rtd_percent: withSupport(csrData.combat_fatigue_rtd_percent),  // mentalHealth rtd_rate_72h × 70, × support
    ptsd_rtd_percent:           withSupport(csrData.ptsd_rtd_percent)             // max(10, rtd_rate_72h × 25), × support
  };

  const expectedRTD = Math.round(
    breakdown.acute_stress_reaction * (rtdExpectations.acute_stress_rtd_percent / 100) +
    breakdown.combat_fatigue        * (rtdExpectations.combat_fatigue_rtd_percent / 100) +
    breakdown.ptsd_onset            * (rtdExpectations.ptsd_rtd_percent / 100)
  );

  return {
    total_csr_casualties: csrCasualties,
    total_csr_unrounded: csrUnrounded,
    rate_per_1000: Math.round(adjustedRate * 10) / 10,

    breakdown,

    rtd_expectations: {
      expected_rtd_count:       expectedRTD,
      expected_evacuation_count: csrCasualties - expectedRTD,
      ...rtdExpectations
    },

    psychiatric_support: {
      level: psychiatricSupport,
      rtd72_multiplier: supportLevel.rtd72Multiplier,
      treatment_days_multiplier: supportLevel.treatmentDaysMultiplier
    },

    factors: {
      threat_level:             threatLevel,
      intensity_key:            csrData.intensity_key,
      base_rate:                CSR_WIA_MODEL.baseline_per_1000_per_day,
      wia_ratio:                csrData.wia_ratio,
      wounded_today:            woundedToday,
      cumulative_battle_casualties: cumulativeBattleCasualties,   // reported only; no longer a multiplier
      duration_factor:          Math.round(durationFactor * 100) / 100,
      casualty_exposure_factor: Math.round(casualtyExposureFactor * 100) / 100,
      cohesion_factor:          cohesionFactor,
      combined_modifier:        Math.round((durationFactor * casualtyExposureFactor * cohesionFactor) * 100) / 100
    },

    // Operational sustainability from mentalHealth.js (days before CSR becomes critical)
    sustainable_days: csrData.sustainable_days,

    recommendations: generateCSRRecommendations(adjustedRate, unitCohesion, dayOfOperation, csrData.sustainable_days),

    educational_note: 'BICEPS principles: Brevity, Immediacy, Centrality, Expectancy, Proximity, Simplicity. ' +
      `At ${csrData.intensity_key}: ${rtdExpectations.acute_stress_rtd_percent}% acute stress RTD within 72h. ` +
      `Sustainable ops: ${csrData.sustainable_days} days. ` +
      'Source: mentalHealth.js research-validated constants (IMP-006 fix).'
  };
}

/**
 * Generate CSR recommendations
 */
function generateCSRRecommendations(rate, cohesion, day, sustainableDays) {
  const recs = [];

  if (rate > 4) {
    recs.push('CRITICAL: High CSR rate - consider unit rotation');
    recs.push('Deploy Combat Stress Control team forward');
  }

  if (cohesion === 'POOR' || cohesion === 'BROKEN') {
    recs.push('Unit cohesion degraded - leadership intervention needed');
    recs.push('Consider reconstitution before further operations');
  }

  if (day > 14) {
    recs.push('Extended operations - implement mandatory rest periods');
  }

  // Sustainable days warning from mentalHealth.js data
  if (sustainableDays && day > sustainableDays * 0.75) {
    recs.push(`Approaching sustainability limit (${sustainableDays} days) - plan rotation`);
  }

  recs.push('Ensure peer support networks active');
  recs.push('Brief leaders on CSR recognition');

  return recs;
}

/**
 * Calculate comprehensive DNBI for a day
 *
 * Integrates all DNBI factors:
 * - Base rate by operational state
 * - Climate modifiers
 * - Altitude effects
 * - Night operations
 * - Combat Stress Reaction
 * - Unit type modifiers
 *
 * @param {Object} config - Complete configuration
 * @returns {Object} Comprehensive DNBI analysis
 */
export function calculateComprehensiveDNBI(config) {
  const {
    strength,
    operationalState = 'COMBAT_ENGAGED',
    unitType = 'MECHANIZED',
    echelon = 'BRIGADE',           // NEW: for echelon-based modifiers
    environment = {},
    climateEnabled = true,         // NEW: flag to enable/disable climate effects
    day = 1,
    cumulativeBattleCasualties = 0,
    woundedToday = 0,              // WIA of the day, drives combat stress volume
    unitCohesion = 'GOOD',
    isNight = false,
    nightConfig = {},
    scenario = null,               // PHASE 1.2: Added for Monte Carlo modifier access
    operationalPosture = null,     // GROUP-1-A: Wojcik three-phase DNBI modifier
    psychiatricSupport = 'STANDARD', // Tab 3 psychiatric support level (constants/psychiatricSupport.js)
    carry = null                    // 2026-10-21: object that keeps the fractions of the daily counts (fractionCarry.js); null rounds each day
  } = config;

  // ENH-034: Theater region DNBI modifier from climateZones.js THEATER_REGIONS.
  // Applied after climate modifier — theater-specific disease profile adjustment.
  const theaterRegion = environment.theater_region || null;
  const theaterDNBIMod = (climateEnabled && theaterRegion)
    ? (THEATER_REGIONS?.[theaterRegion]?.dnbi_modifier ?? 1.0)
    : 1.0;

  // 1. Get base DNBI rate from operational state
  // TASK 3.0 FIX: Corrected fallback values to match operationalStates.js constants
  // P1-01 FIX: Key in dnbiRates.js is `combined_rate`, NOT `rate_per_1000_per_day`.
  // Using `rate_per_1000_per_day` always returned undefined → fallback fired 100% of the time.
  const stateRate = DNBI_RATES_BY_OPERATIONAL_STATE?.[operationalState]?.combined_rate ||
    getStateDNBIRate?.(operationalState) ||
    (operationalState === 'COMBAT_ENGAGED' ? 4.5 :    // FIX: was 2.0
     operationalState === 'SUPPORT_ACTIVE' ? 1.5 :    // FIX: was 1.0
     operationalState === 'TRUE_RESERVE' ? 0.5 :      // FIX: was 0.7 (in else clause)
     4.5);  // Default to Combat Engaged for unknown states

  // 2. Get unit type modifier
  const unitTypeConfig = UNIT_TYPES?.[unitType] || { dnbi_modifier: 1.0 };
  const unitTypeMod = unitTypeConfig.dnbi_modifier || 1.0;

  // 3. Get echelon modifier (affects psych/CSR rates)
  // Battalions closer to combat have higher psych stress (1.2×)
  // Corps/Theater rear areas have lower (0.8×)
  const echelonData = DNBI_RATES_BY_ECHELON?.[echelon] ||
                      DNBI_RATES_BY_ECHELON?.BRIGADE ||
                      { psych_multiplier: 1.0, weighted_daily_rate: 0.16 };
  const echelonPsychMod = echelonData.psych_multiplier || 1.0;

  // 4. Calculate climate modifier (only if climate is enabled)
  const climateAnalysis = climateEnabled ? calculateClimateModifier({
    climateZone: environment.climate || 'TEMPERATE',
    season: environment.season || 'summer',
    month: environment.month || null,          // NEW: monthly precision
    altitude_m: environment.altitude_m || 500,
    terrain: environment.terrain || 'OPEN',    // NEW: terrain from DNBI_ENVIRONMENTAL_MODIFIERS
    sanitation: environment.water_food_safety || 'GOOD', // NEW: sanitation level
    endemic_disease: environment.endemic_disease || 'NONE',
    prophylaxis_compliance: environment.prophylaxis_compliance ?? 1.0
  }) : {
    modifiers: { combined: 1.0, disease_combined: 1.0, injury_combined: 1.0 },
    primary_risks: [],
    recommendations: []
  };

  // 5. Calculate night operations effect
  const nightAnalysis = calculateNightOperationsDNBI(isNight, nightConfig);

  // 6. Calculate CSR separately (added to total, not multiplied)
  // Apply echelon psych modifier to CSR
  const csrAnalysis = calculateCSRCasualties({
    strength,
    threatLevel: environment.threat_level || 'MODERATE_THREAT',
    dayOfOperation: day,
    cumulativeBattleCasualties,
    woundedToday,
    unitCohesion,
    psychiatricSupport
  });

  // Adjust CSR by echelon (battalion = more stress, corps = less stress)
  const rawCSRAdjusted = csrAnalysis.total_csr_casualties * echelonPsychMod;
  // 2026-10-21: with a carry object the unrounded combat stress count is rounded with the carry (fractions go to the next day)
  const rawCSRCarried = carry ? csrAnalysis.total_csr_unrounded * echelonPsychMod : rawCSRAdjusted;
  const adjustedCSRCasualties = isNaN(rawCSRCarried) ? 0 : roundWithCarry(rawCSRCarried, carry, 'csr');

  // 7. Calculate non-CSR DNBI
  // PHASE 1.2: Monte Carlo modifier (if present)
  // This modifier varies DNBI rates within research-validated ranges (±25%)
  // Only present when Monte Carlo simulation is running (monteCarloSimulator.js)
  // CRITICAL FIX: Read from operation_parameters.monte_carlo_modifiers (not scenario root)
  const monteCarloMod = scenario?.operation_parameters?.monte_carlo_modifiers?.dnbi_modifier || 1.0;

  // ENH-032: Apply NIGHT_OPS_LEVELS.casualty_modifier from nightOperations.js.
  // Tab2 pre-computes getNightOpsModifiers() and stores result at night_ops_config.modifiers.
  // Values: NONE=1.0, LIMITED=1.05, MODERATE=1.1, HIGH=1.15, CONTINUOUS=1.2.
  // Stacks on top of calculateNightOperationsDNBI() which handles the binary isNight boolean.
  const _nightOpsCfg = scenario?.operation_parameters?.night_ops_config;
  const nightOpsCasualtyMod = (_nightOpsCfg?.enabled && _nightOpsCfg?.modifiers?.casualty_modifier)
    ? (_nightOpsCfg.modifiers.casualty_modifier ?? 1.0)
    : 1.0;

  // ENH-045: Apply Force Health Protection (FHP) level modifier from climateZones.js FHP_LEVELS.
  // fhp_config.fhp_level set in Tab2 (or defaults to STANDARD = 1.0 = no change).
  // NONE=1.25 (+25% DNBI), MINIMAL=1.10, STANDARD=1.00 (baseline), OPTIMAL=0.75 (−25% DNBI).
  const _fhpLevel = scenario?.operation_parameters?.fhp_config?.fhp_level || 'STANDARD';
  const fhpDNBIMod = FHP_LEVELS[_fhpLevel]?.dnbi_modifier ?? 1.0;

  // GROUP-1-A: Wojcik three-phase DNBI model (Wojcik & Humphrey 2004/2008/2016).
  // Maps the current phase's operational_posture to a Wojcik phase category
  // (BUILD_UP / GROUND_COMBAT / POST_COMBAT) and applies the corresponding modifier.
  // This adds a temporal/phase dimension on top of the operational-state base rate:
  //   - Ground combat postures (ATTACK, DEFENSE, etc.): modifier = 1.00 (no change)
  //   - Post-combat postures (RESERVE, STABILITY_OPERATIONS): modifier = 0.65
  //   - Build-up postures (MARCH): modifier = 0.50 (march DNBI path usually pre-empts this)
  // No-op (1.00) when operationalPosture is null/unknown — preserves backward compatibility.
  const _wojcikPhase = operationalPosture
    ? (DNBI_WOJCIK_POSTURE_MAP[operationalPosture] || 'GROUND_COMBAT')
    : 'GROUND_COMBAT';
  const wojcikPhaseMod = DNBI_WOJCIK_PHASE_RATES[_wojcikPhase]?.phase_modifier ?? 1.00;

  // V4-002: climate_severity — MC uncertainty around the already-configured climate zone modifier.
  // climate_severity > 1.0 = worse-than-planned conditions (heat wave, cold snap, monsoon).
  // Stacks multiplicatively with existing climateAnalysis.modifiers.combined.
  const _mcClimateSevMod = scenario?.operation_parameters?.monte_carlo_modifiers?.climate_severity ?? 1.0;

  // V4-007: CBRN DNBI elevation factor.
  // Source: AJMedP-7.5 Table 3 + NATO STANAG 2553 (AMedP-7.5).
  // MOPP conditions (heat stress, dehydration, psychological burden, secondary contamination casualties)
  // elevate DNBI above the standard baseline. Pre-V4-007 scenarios have no cbrn_threat_level field
  // and fall back to ?? 'NONE' => multiplier 1.0 -- no change to existing results.
  // 2026-10-13 (Tab 2 plan 7.1): the Threat Assessment panel saves `threat_assessment.cbrn_risk` (same four keys); this module read only
  // `cbrn_threat_level`, so the panel did nothing. The explicit field still wins.
  const _cbrnLevel   = scenario?.operation_parameters?.cbrn_threat_level ?? scenario?.operation_parameters?.threat_assessment?.cbrn_risk ?? 'NONE';
  const _cbrnDNBIMod = CBRN_THREAT_LEVELS[_cbrnLevel]?.dnbi_multiplier ?? 1.0;

  // 2026-10-13 (Tab 2 plan 7.5): the threat type acts on the non-battle burden only (x1.0 conventional, 1.2 asymmetric, 1.3 hybrid, 1.5
  // peacekeeping and high intensity); its casualty multiplier is not applied (the enemy resistance of the phase replaces it).
  const _threatTypeDNBIMod = THREAT_EFFECTS_USE ? threatTypeDnbiFactor(scenario?.operation_parameters?.threat_assessment || {}) : 1.0;
  // 2026-10-13 (Tab 2 plan 7.10): the water supply of the infrastructure card (municipal unreliable x1.1, well water x1.2)
  const _infraDNBIMod = infrastructureModel(scenario?.operation_parameters?.infrastructure_config)?.dnbi_modifier ?? 1.0;
  const combinedModifier = _infraDNBIMod * unitTypeMod * climateAnalysis.modifiers.combined * nightAnalysis.modifier * monteCarloMod * theaterDNBIMod * nightOpsCasualtyMod * fhpDNBIMod * wojcikPhaseMod * _mcClimateSevMod * _cbrnDNBIMod * _threatTypeDNBIMod;
  const adjustedRate = stateRate * combinedModifier;

  // DIAGNOSTIC: Trace NaN source (BUG-046)
  if (isNaN(strength) || strength === undefined || strength === null) {
  }
  if (isNaN(stateRate)) {
  }
  if (isNaN(unitTypeMod)) {
  }
  if (isNaN(climateAnalysis?.modifiers?.combined)) {
  }
  if (isNaN(nightAnalysis?.modifier)) {
  }

  // Safe fallback to prevent NaN cascade
  const safeStrength = isNaN(strength) || !strength ? 0 : strength;
  const safeRate = isNaN(adjustedRate) ? 0 : adjustedRate;
  const nonCSRDNBI = roundWithCarry((safeStrength / 1000) * safeRate, carry, 'nonCsr');

  // 8. Total DNBI = non-CSR + CSR (adjusted by echelon)
  const totalDNBI = nonCSRDNBI + adjustedCSRCasualties;

  // 9. Calculate category breakdown using Largest Remainder Method
  // This ensures distributed total EXACTLY equals nonCSRDNBI (no rounding errors)
  const categoryBreakdown = {};

  // First, handle BEHAVIORAL_HEALTH separately (it's CSR, not nonCSRDNBI)
  categoryBreakdown.BEHAVIORAL_HEALTH = adjustedCSRCasualties;

  // Get non-BH categories and their adjusted percentages.
  // CALC-014: the constants call the behavioural-health category PSYCHIATRIC. The code looked for
  // BEHAVIORAL_HEALTH (absent), used a literal 25%, and still counted PSYCHIATRIC among the other
  // categories, so behavioural health appeared twice and the categories over-allocated the total.
  const bhPercentage = requireNumber(DNBI_CATEGORY_BREAKDOWN[BEHAVIORAL_HEALTH_SOURCE_CATEGORY]?.percentage,
    'dnbiModule.calculateComprehensiveDNBI', `DNBI_CATEGORIES.${BEHAVIORAL_HEALTH_SOURCE_CATEGORY}.percentage`);
  const nonBHCategories = Object.entries(DNBI_CATEGORY_BREAKDOWN)
    .filter(([category]) => category !== BEHAVIORAL_HEALTH_SOURCE_CATEGORY)
    .map(([category, data]) => ({
      category,
      percentage: data.percentage,
      adjustedPercent: data.percentage / (1 - bhPercentage)
    }));

  // Calculate proportional values with fractional parts
  const withFractions = nonBHCategories.map(cat => {
    const exactValue = nonCSRDNBI * cat.adjustedPercent;
    return {
      ...cat,
      exactValue,
      floorValue: Math.floor(exactValue),
      remainder: exactValue - Math.floor(exactValue)
    };
  });

  // Initial distribution using floor values
  let distributedTotal = withFractions.reduce((sum, cat) => sum + cat.floorValue, 0);
  let remaining = nonCSRDNBI - distributedTotal;

  // Sort by largest remainder to distribute remaining casualties fairly
  withFractions.sort((a, b) => b.remainder - a.remainder);

  // Assign floor values first
  for (const cat of withFractions) {
    categoryBreakdown[cat.category] = cat.floorValue;
  }

  // Distribute remaining casualties to categories with largest remainders
  let idx = 0;
  while (remaining > 0 && idx < withFractions.length) {
    categoryBreakdown[withFractions[idx].category]++;
    remaining--;
    idx++;
  }

  // 10. Calculate RTD expectations
  let expectedRTD = 0;
  let expectedRTDUnrounded = 0;   // carry: the non-CSR share of the return to duty is rounded once, with the carry
  for (const [category, count] of Object.entries(categoryBreakdown)) {
    if (category === 'BEHAVIORAL_HEALTH') {
      expectedRTD += csrAnalysis.rtd_expectations.expected_rtd_count;
    } else {
      const evacuationRate = requireNumber(DNBI_CATEGORY_BREAKDOWN[category]?.evacuation_rate,
        'dnbiModule.calculateComprehensiveDNBI', `DNBI_CATEGORIES.${category}.evacuation_rate`);
      expectedRTD += Math.round(count * (1 - evacuationRate));
      expectedRTDUnrounded += count * (1 - evacuationRate);
    }
  }
  if (carry) {
    expectedRTD = roundWithCarry(expectedRTDUnrounded, carry, 'rtd') + (categoryBreakdown.BEHAVIORAL_HEALTH !== undefined ? csrAnalysis.rtd_expectations.expected_rtd_count : 0);
  }

  return {
    total_dnbi: totalDNBI,
    rate_per_1000: Math.round(adjustedRate * 10) / 10,

    breakdown: {
      non_csr_dnbi: nonCSRDNBI,
      csr_casualties: adjustedCSRCasualties,  // Use echelon-adjusted CSR
      csr_raw: csrAnalysis.total_csr_casualties,  // Original CSR before echelon
      by_category: categoryBreakdown
    },

    rtd: {
      expected_rtd: expectedRTD,
      expected_evacuation: totalDNBI - expectedRTD,
      rtd_rate: Math.round((expectedRTD / Math.max(1, totalDNBI)) * 100)
    },

    // Psychiatric support in force (Tab 3) and what it does to combat stress return to duty and treatment days
    psychiatric_support: csrAnalysis.psychiatric_support,

    modifiers: {
      operational_state: {
        state: operationalState,
        base_rate: stateRate
      },
      unit_type: {
        type: unitType,
        modifier: unitTypeMod
      },
      echelon: {
        level: echelon,
        psych_modifier: echelonPsychMod,
        weighted_rate: echelonData.weighted_daily_rate || 0.16,
        note: echelonPsychMod > 1 ?
              `${echelon} level increases psych stress by ${Math.round((echelonPsychMod - 1) * 100)}%` :
              echelonPsychMod < 1 ?
              `${echelon} level reduces psych stress by ${Math.round((1 - echelonPsychMod) * 100)}%` :
              'Baseline psych stress level'
      },
      climate: climateAnalysis,
      climate_enabled: climateEnabled,
      night: nightAnalysis,
      night_ops_casualty_modifier: nightOpsCasualtyMod,  // ENH-032: NIGHT_OPS_LEVELS.casualty_modifier
      // GROUP-1-A: Wojcik three-phase model outputs — available for Tab5/6 display
      wojcik_phase: _wojcikPhase,
      wojcik_phase_modifier: wojcikPhaseMod,
      // V4-007: CBRN DNBI elevation factor (AJMedP-7.5 Table 3 + STANAG 2553 (AMedP-7.5))
      cbrn_threat_level: _cbrnLevel,
      cbrn_dnbi_modifier: _cbrnDNBIMod,
      combined: Math.round(combinedModifier * 100) / 100
    },

    csr_analysis: {
      ...csrAnalysis,
      echelon_adjusted: adjustedCSRCasualties,
      echelon_modifier: echelonPsychMod
    },

    specific_risks: climateAnalysis.specific_risk_rates || [],

    recommendations: [
      ...(climateAnalysis.recommendations || []),
      ...(nightAnalysis.recommendations || []),
      ...(csrAnalysis.recommendations || [])
    ],

    educational_insight: generateDNBIEducationalInsight(
      operationalState,
      environment.climate,
      csrAnalysis.rate_per_1000,
      totalDNBI
    )
  };
}

/**
 * Generate DNBI educational insight
 */
function generateDNBIEducationalInsight(state, climate, csrRate, total) {
  let insight = '';

  insight += `Operational state '${state}' drives base DNBI rate. `;

  if (climate && climate !== 'TEMPERATE') {
    insight += `${climate} climate significantly increases DNBI - `;
    if (climate === 'ARCTIC') insight += 'cold injuries dominant. ';
    else if (climate === 'DESERT') insight += 'heat injuries and dehydration risks. ';
    else if (climate.includes('TROPICAL')) insight += 'disease prevention critical. ';
  }

  if (csrRate > 3) {
    insight += `High CSR rate (${csrRate}/1000/day) - `;
    insight += 'forward mental health support essential. ';
  }

  insight += `Total DNBI: ${total} - typically 2-3x battle casualties in sustained operations.`;

  return insight;
}

// ============================================================================
// EXPORTS
// ============================================================================

// Named exports for direct use
export {
  CLIMATE_ZONE_MAPPING,
  ALTITUDE_EFFECTS,
  NIGHT_OPERATIONS_DNBI,
  CSR_RATES,
  DNBI_CATEGORY_BREAKDOWN,
  getClimateData,
  getTerrainData,
  getSanitationData
};

// Re-export from dnbiRates.js for convenience
export { DNBI_ENVIRONMENTAL_MODIFIERS } from '../../constants/dnbiRates.js';

// Named default export (fixes lint warning)
const dnbiModule = {
  // Main calculation functions
  calculateClimateModifier,
  calculateNightOperationsDNBI,
  calculateCSRCasualties,
  calculateComprehensiveDNBI,

  // Helper functions
  getClimateData,
  getTerrainData,
  getSanitationData,

  // Constants
  CLIMATE_ZONE_MAPPING,
  ALTITUDE_EFFECTS,
  NIGHT_OPERATIONS_DNBI,
  CSR_RATES,
  DNBI_CATEGORY_BREAKDOWN
};

export default dnbiModule;
